'use client'

import { useEffect } from 'react'

import { useSearchParams } from 'next/navigation'
import Script from 'next/script'

import { COOKIEPRO_SCRIPT_SRC } from '@/constants/cookie-consent'
import { IFRAME_SEARCH_PARAM_KEYS } from '@/constants/iframe'
import type { Locale } from '@/constants/locales'
import { useIsEmbedded } from '@/hooks/useIsEmbedded'

const optanonWrapperScript = `
window.OptanonWrapper = window.OptanonWrapper || function OptanonWrapper() {};
`

const COOKIEPRO_LOCALE_OVERRIDES = new Map([
  ['zh-TW', 'zh-Hant'],
  ['pt-BR', 'pt-BR'],
  ['pl-PL', 'pl-PL'],
])

const getCookieProLocale = (locale: Locale): string => {
  return COOKIEPRO_LOCALE_OVERRIDES.get(locale) ?? locale.split('-', 1)[0]
}

const syncCookieProLocale = (locale: Locale): void => {
  void window.OneTrust?.changeLanguage(getCookieProLocale(locale))
}

type CookieProScriptClientProps = {
  domainScriptId: string
  locale: Locale
}

export default function CookieProScriptClient({
  domainScriptId,
  locale,
}: CookieProScriptClientProps) {
  const searchParams = useSearchParams()
  const isEmbedded = useIsEmbedded(true)
  const isNoCookie = searchParams.has(IFRAME_SEARCH_PARAM_KEYS.NO_COOKIE)

  useEffect(() => {
    if (isEmbedded || isNoCookie) return

    syncCookieProLocale(locale)
  }, [isEmbedded, isNoCookie, locale])

  if (isEmbedded || isNoCookie) return null

  return (
    <>
      <Script
        id="cookiepro-optanon-wrapper"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: optanonWrapperScript }}
      />
      <Script
        id="cookiepro-sdk"
        src={COOKIEPRO_SCRIPT_SRC}
        strategy="afterInteractive"
        data-domain-script={domainScriptId}
        data-document-language="true"
        onLoad={() => syncCookieProLocale(locale)}
      />
    </>
  )
}
